'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/scale-extra'

const LRSchedule = dynamic(() => import('../_components/widgets/ScaleWidgets').then(m => m.LRSchedule), { ssr: false })
const MemoryCalculator = dynamic(() => import('../_components/widgets/ScaleWidgets').then(m => m.MemoryCalculator), { ssr: false })

export default function TrainingAtScale() {
  return (
    <Lesson>
      <Step>
        <H>From your laptop to a data centre</H>
        <P>
          The model you trained in the tiny-GPT lesson had 60 000 parameters and took a minute. Frontier models have hundreds
          of billions of parameters and train for months on thousands of GPUs. The ideas are the same ones you know. What
          changes is engineering: keeping a gigantic, expensive run stable, and fitting it into hardware at all.
        </P>
        <Figure n="9.8" wide caption="Pick a model size. Training memory is about 16 bytes per parameter before you even store the activations. Then pick how much data to train on, and see the compute bill.">
          <MemoryCalculator />
        </Figure>
      </Step>

      <Step gate="compute-budget">
        <H>Back-of-the-envelope maths</H>
        <P>
          Two rules of thumb get you surprisingly far. Training costs about <M>{'6ND'}</M> floating-point operations for a model
          with <M>N</M> parameters trained on <M>D</M> tokens: 2 for the forward pass and 4 for the backward pass, per parameter
          per token. And mixed-precision training with Adam needs about 16 bytes of memory per parameter.
        </P>
        <Exercise
          id="compute-budget"
          title="trainingFlops, trainingMemoryGB, days"
          {...EX.computeBudget}
          hints={[
            <>
              <C>{'6 * params * tokens'}</C> and <C>{'16 * params / 1e9'}</C>
            </>,
            <>
              <C>{'flops / (gpus * flopsPerGpu) / 86400'}</C>
            </>,
          ]}
        />
      </Step>

      <Step gate="lr-schedule">
        <H>Warm up, then cool down</H>
        <P>
          At the very start, the weights are random and the gradients are wild. A full-size step can throw the model somewhere
          it never recovers from. So big runs <strong>warm up</strong>: the learning rate rises from almost zero over the first
          few hundred or thousand steps. Then it slowly <strong>decays</strong>, so that late in training the steps are small
          enough to settle into a good spot.
        </P>
        <Figure n="9.9" caption="Warm-up followed by cosine decay, the most common schedule for training transformers.">
          <LRSchedule />
        </Figure>
        <Exercise
          id="lr-schedule"
          title="lrAt"
          {...EX.lrSchedule}
          hints={[
            <>
              <C>{'if (step < warmup) return maxLr * (step + 1) / warmup'}</C>
            </>,
            <>
              Then compute <C>p</C> and <C>{'minLr + (maxLr - minLr) * 0.5 * (1 + Math.cos(Math.PI * p))'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="clipping">
        <H>Gradient clipping</H>
        <P>
          Over millions of steps, the occasional batch produces a gigantic gradient: a <strong>loss spike</strong>. One huge
          step can undo weeks of training. Clipping caps the length of the whole gradient vector, keeping its direction but
          limiting how far one step can go.
        </P>
        <MB>{'\\mathbf{g} \\leftarrow \\mathbf{g} \\cdot \\min\\!\\left(1,\\; \\frac{\\text{maxNorm}}{\\lVert \\mathbf{g} \\rVert}\\right)'}</MB>
        <Exercise
          id="clipping"
          title="globalNorm and clip"
          {...EX.clipping}
          hints={[
            <>
              <C>{'for (const g of grads) for (const v of g) s += v * v'}</C>, then <C>Math.sqrt(s)</C>.
            </>,
            <>
              <C>{'const f = n > maxNorm ? maxNorm / n : 1'}</C>, then multiply every entry by <C>f</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate={['accumulation', 'q-parallel']}>
        <H>Splitting the work</H>
        <P>
          A batch of a few million tokens doesn’t fit on one GPU. But the gradient of an average is the average of the
          gradients. Split the batch into pieces, compute each piece’s gradient separately, add them up, and the result is
          exactly the same as the gradient of the whole batch.
        </P>
        <Exercise
          id="accumulation"
          title="Gradient accumulation"
          {...EX.accumulation}
          hints={[
            <>
              <C>{'const s = 16 / k'}</C>; for each micro-batch, <C>{'layer.forward(xb).crossEntropy(y.slice(i * s, (i + 1) * s)).mul(1 / k).backward()'}</C>.
            </>,
          ]}
        />
        <UL>
          <li>
            <strong>Gradient accumulation</strong>: one GPU processes the pieces one after another, adding up gradients, then
            takes one step.
          </li>
          <li>
            <strong>Data parallelism</strong>: many GPUs each process a different piece at the same time, then average their
            gradients over the network.
          </li>
          <li>
            <strong>Model parallelism</strong>: when the model itself is too big for one GPU, its layers (or even single matrix
            multiplies) are split across GPUs.
          </li>
        </UL>
        <Quiz
          id="q-parallel"
          question="1 000 GPUs train with data parallelism. What must they exchange after every step?"
          options={[
            { label: 'Their gradients, so everyone can apply the same averaged update', correct: true },
            { label: 'Their training data', why: 'Each GPU keeps its own share of the data. What must match is the update.' },
            { label: 'Nothing — they train independently and are averaged at the end', why: 'Then they’d drift into different models. They synchronise every step.' },
          ]}
          explain="That exchange, called an all-reduce, moves gigabytes every step. It’s why AI data centres need extraordinarily fast networks between GPUs."
        />
        <Callout kind="note">
          One more trick from Module 2: <strong>mixed precision</strong>. The heavy matrix multiplies run in 16-bit bfloat16,
          about twice as fast and half the memory, while a 32-bit master copy of the weights takes the small updates without
          rounding them away.
        </Callout>
      </Step>
    </Lesson>
  )
}
